import { useState } from 'react'
import { createUrlMedia } from '@wiggot/utils'
import { useModalBrokerInvitationStore } from '../../../../../../store/modals-services/modal-broker-invitation'
import { useSession } from '@wiggot/auth/hooks/useSession'
import {
  IAuthUser,
  IProfileUser,
  IResponseService
} from '../../../../../../../interfaces/src'
import { defaultApiBasicX, legacyApiAuth } from '@wiggot/apis'
import { updateCookieAuth, updateCookieProfile } from '../../../../../../utils'
import { postHandleInvitationCollaborators } from '../../../../../../services/invitations-services'

export const updateRoles = async (): Promise<IResponseService<string[]>> => {
  const response = await legacyApiAuth.get<string[]>(`/v2/user/roles`)
  try {
    return {
      isSuccess: true,
      data: response
    }
  } catch (error: any) {
    return {
      isSuccess: false,
      error
    }
  }
}

export const getBinaryRole = async (
  userId: string
): Promise<{
  isSuccess: boolean
  binaryRoles?: number
  error?: any
}> => {
  try {
    const response = await defaultApiBasicX.get<any>(
      `/users-management/users`,
      {
        userId
      }
    )
    return {
      isSuccess: true,
      binaryRoles: response?.data?.roles
    }
  } catch (error: any) {
    return {
      isSuccess: false,
      error
    }
  }
}

export const useCompleted = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | undefined>(undefined)
  const { updateAuthUser, updateProfile, profile, authUser } = useSession()
  const invitationData = useModalBrokerInvitationStore(
    (state) => state.invitationData
  )
  const setIsOpen = useModalBrokerInvitationStore((state) => state.setIsOpen)

  const reloadPage = () => {
    setTimeout(() => {
      setIsLoading(false)
      window.location.reload()
    }, 100)
  }

  const logoUrl = invitationData?.logo
    ? createUrlMedia({
        id: invitationData?.logo
      })
    : undefined

  const addRolesInCookies = async ({
    newBinaryRoles,
    enableAdminRoles
  }: {
    newBinaryRoles: number
    enableAdminRoles: boolean
  }) => {
    if (authUser) {
      const brokerRoles = { AGENCY_BROKER: 'AGENCY_BROKER', BROKER: 'BROKER' }

      const handleNewRoles = enableAdminRoles
        ? { ...brokerRoles, AGENCY: 'AGENCY' }
        : brokerRoles

      const newRoles: IAuthUser['roles'] = {
        ...authUser?.roles,
        ...handleNewRoles
      }

      const changeAuthUser: IAuthUser = {
        ...authUser,
        binaryRoles: newBinaryRoles,
        roles: {
          ...authUser?.roles,
          ...newRoles
        }
      }

      const newAuthUser = updateCookieAuth(authUser, changeAuthUser)
      updateAuthUser(newAuthUser)
    }

    if (profile) {
      const changeProfile: IProfileUser = {
        ...profile,
        agencyId: Number(invitationData?.agencyId),
        agencyName: invitationData?.agencyName
      }

      const newProfile = updateCookieProfile(profile, changeProfile)
      updateProfile(newProfile)
    }
  }

  const handleCompleted = async () => {
    const rolesInvitation = invitationData?.senderUser?.newRoles || []
    const newRolesIncludedAgency = rolesInvitation?.includes('AGENCY')

    if (
      (authUser?.roles?.AGENCY_BROKER &&
        authUser?.roles?.BROKER &&
        authUser?.roles?.AGENCY &&
        newRolesIncludedAgency) ||
      (authUser?.roles?.AGENCY_BROKER &&
        authUser?.roles?.BROKER &&
        !newRolesIncludedAgency)
    ) {
      postHandleInvitationCollaborators({
        inviteId: invitationData?.inviteId || '',
        statusAccepted: true
      })
      setIsOpen({
        isOpen: false,
        brokerAgencyRequestExists: false,
        invitationData: undefined,
        status: undefined
      })
      return
    }

    setIsLoading(true)
    try {
      const [binaryRoleResponse, rolesUpdateResponse, hiddenModalResponse] =
        await Promise.all([
          getBinaryRole(profile?.userId?.toString() || ''),
          updateRoles(),
          postHandleInvitationCollaborators({
            inviteId: invitationData?.inviteId || '',
            statusAccepted: true
          })
        ])

      if (
        binaryRoleResponse?.isSuccess &&
        binaryRoleResponse?.binaryRoles &&
        rolesUpdateResponse?.isSuccess &&
        hiddenModalResponse?.isSuccess
      ) {
        addRolesInCookies({
          enableAdminRoles: newRolesIncludedAgency,
          newBinaryRoles: binaryRoleResponse?.binaryRoles
        })
        reloadPage()
      } else {
        setIsLoading(false)
        setError(
          'Ocurrio un error al actualizar datos, por favor intenta nuevamente'
        )
      }
    } catch (error: unknown) {
      console.error(error)
      setIsLoading(false)
      setError(
        'Ocurrio un error al actualizar datos, por favor intenta nuevamente'
      )
    }
  }

  return {
    logoUrl,
    invitationData,
    isLoading,
    error,
    handleCompleted
  }
}
